import React from 'react';
import PropTypes from 'prop-types';

/**
 * HealthChip
 *
 * Renders one of the seven DatasetHealth verdicts (design spec 9) as a
 * colored pill with its human-readable label. `reason` (the backend's
 * `health_reason`, e.g. "412 of 1,904 properties current (22%)") is
 * surfaced as a hover tooltip.
 *
 * Tone mapping matches the backend enum's own inline annotations exactly:
 * warming_up/no_watchers are deliberately neutral (grey), never red - a
 * fresh install with no history yet, or a dataset nobody watches, is not a
 * failure. job_not_running/data_in_no_email are red (real failures);
 * sends_failing/degraded_coverage are amber (degraded, not down); healthy
 * is green.
 */

export const DATASET_HEALTH_WARMING_UP = 'warming_up';
export const DATASET_HEALTH_JOB_NOT_RUNNING = 'job_not_running';
export const DATASET_HEALTH_DATA_IN_NO_EMAIL = 'data_in_no_email';
export const DATASET_HEALTH_SENDS_FAILING = 'sends_failing';
export const DATASET_HEALTH_DEGRADED_COVERAGE = 'degraded_coverage';
export const DATASET_HEALTH_NO_WATCHERS = 'no_watchers';
export const DATASET_HEALTH_HEALTHY = 'healthy';

const CHIP_TONE_GREY = 'grey';
const CHIP_TONE_RED = 'red';
const CHIP_TONE_AMBER = 'amber';
const CHIP_TONE_GREEN = 'green';

const DEFAULT_TONE = CHIP_TONE_GREY;

/**
 * Canonical DatasetHealth evaluation order (design spec 9: first match
 * wins). Exported so the container's health filter dropdown lists options
 * in this same order rather than a second hand-maintained copy.
 */
export const ALL_HEALTH_VALUES = [
	DATASET_HEALTH_WARMING_UP,
	DATASET_HEALTH_JOB_NOT_RUNNING,
	DATASET_HEALTH_DATA_IN_NO_EMAIL,
	DATASET_HEALTH_SENDS_FAILING,
	DATASET_HEALTH_DEGRADED_COVERAGE,
	DATASET_HEALTH_NO_WATCHERS,
	DATASET_HEALTH_HEALTHY,
];

/**
 * DatasetHealth value -> chip tone. Exported so DatasetRow can paint the
 * compact column-1 health dot with the same tone without re-deriving it.
 */
export const HEALTH_TONE = {
	[DATASET_HEALTH_WARMING_UP]: CHIP_TONE_GREY,
	[DATASET_HEALTH_JOB_NOT_RUNNING]: CHIP_TONE_RED,
	[DATASET_HEALTH_DATA_IN_NO_EMAIL]: CHIP_TONE_RED,
	[DATASET_HEALTH_SENDS_FAILING]: CHIP_TONE_AMBER,
	[DATASET_HEALTH_DEGRADED_COVERAGE]: CHIP_TONE_AMBER,
	[DATASET_HEALTH_NO_WATCHERS]: CHIP_TONE_GREY,
	[DATASET_HEALTH_HEALTHY]: CHIP_TONE_GREEN,
};

/**
 * DatasetHealth value -> human label. Exported so the container's health
 * filter dropdown lists the same wording as the chip itself.
 */
export const HEALTH_LABEL = {
	[DATASET_HEALTH_WARMING_UP]: 'Warming up',
	[DATASET_HEALTH_JOB_NOT_RUNNING]: 'Job not running',
	[DATASET_HEALTH_DATA_IN_NO_EMAIL]: 'Data in, no email',
	[DATASET_HEALTH_SENDS_FAILING]: 'Sends failing',
	[DATASET_HEALTH_DEGRADED_COVERAGE]: 'Degraded coverage',
	[DATASET_HEALTH_NO_WATCHERS]: 'No watched properties',
	[DATASET_HEALTH_HEALTHY]: 'Healthy',
};

/**
 * DatasetHealth value -> plain-language definition of what the verdict means,
 * worded for an admin who has never read the health rules (design spec 9).
 * Exported so the summary tiles explain themselves without a second copy of
 * this wording.
 */
export const HEALTH_DESCRIPTION = {
	[DATASET_HEALTH_WARMING_UP]:
		'There is not enough history yet to judge this dataset. It has been recording for less than one full run cycle, or has never recorded a run at all.',
	[DATASET_HEALTH_JOB_NOT_RUNNING]:
		'No run has been recorded for more than twice this dataset\'s normal interval, so its scraper looks stopped rather than merely late.',
	[DATASET_HEALTH_DATA_IN_NO_EMAIL]:
		'New records landed long enough ago that a digest should already have gone out, but nobody was emailed and nothing was deliberately suppressed.',
	[DATASET_HEALTH_SENDS_FAILING]:
		'At least one alert or notification email for this dataset failed to send inside the selected window.',
	[DATASET_HEALTH_DEGRADED_COVERAGE]:
		'Fewer than 80% of active properties were refreshed for this dataset inside the selected window, so the data is going stale for the rest.',
	[DATASET_HEALTH_NO_WATCHERS]:
		'No portfolio subscribes to this dataset, so no emails are expected from it. This is not a fault.',
	[DATASET_HEALTH_HEALTHY]:
		'Running on schedule, refreshing enough properties, and sending its emails without failures.',
};

/**
 * The rule behind each verdict, in the reader's terms. Shown in the legend
 * beside HEALTH_DESCRIPTION, which says what the verdict means; this says when
 * it fires.
 */
export const HEALTH_THRESHOLD = {
	[DATASET_HEALTH_WARMING_UP]: 'Less than one run cycle of history, or no run ever recorded.',
	[DATASET_HEALTH_JOB_NOT_RUNNING]:
		'No run for more than twice the normal interval: 8 hours for a four-hourly dataset, 48 hours for a daily one, 2 weeks for a weekly one.',
	[DATASET_HEALTH_DATA_IN_NO_EMAIL]:
		'Records landed over 26 hours ago (one digest cycle plus margin), the dataset emails, someone subscribes, and yet nothing was sent or suppressed.',
	[DATASET_HEALTH_SENDS_FAILING]: 'One or more emails failed to send inside the window.',
	[DATASET_HEALTH_DEGRADED_COVERAGE]:
		'Under 80% of active properties refreshed inside the window.',
	[DATASET_HEALTH_NO_WATCHERS]: 'No portfolio subscribes to this dataset.',
	[DATASET_HEALTH_HEALTHY]: 'None of the rules above matched.',
};

const HealthChip = ({ health, reason }) => {
	const tone = HEALTH_TONE[health] || DEFAULT_TONE;
	const label = HEALTH_LABEL[health] || health || 'Unknown';

	return (
		<span className={`health-chip chip-${tone}`} title={reason || undefined}>
			<span className="health-chip-dot" aria-hidden="true" />
			{label}
		</span>
	);
};

HealthChip.propTypes = {
	health: PropTypes.string,
	reason: PropTypes.string,
};

HealthChip.defaultProps = {
	health: undefined,
	reason: undefined,
};

export default HealthChip;
